"use client";

import React, { useState, useEffect } from "react";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Globe, Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="29148" className="hidden lg:flex gap-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-20 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu className="hidden lg:flex">
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild className={navigationMenuTriggerStyle()}>
              <Link to={item.href || "#"} className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors bg-transparent hover:bg-transparent focus:bg-transparent data-[active]:bg-transparent underline-offset-4 hover:underline tracking-tight">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-8 w-full bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-6 p-6">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-lg font-medium text-foreground hover:text-primary transition-colors border-b border-border/50 pb-2" onClick={onClose}>
          {item.label}
        </Link>)}
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="halforder-header" className={cn("dark sticky top-0 z-50 w-full transition-all duration-300 ease-in-out", isScrolled ? "bg-background/80 backdrop-blur-md border-b border-white/10 shadow-lg" : "bg-background border-b border-transparent")}>
      <div className="container mx-auto px-4 md:px-8 h-20 flex items-center justify-between">
        {}
        <div className="flex-shrink-0">
          <Link to="/HomePage">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1611703/img/ride_with_us_logo.png" alt="HalfOrder" className="h-7 w-auto object-contain" />
          </Link>
        </div>

        {}
        <div className="hidden lg:block">
          <MenuProvider menu_id="16" className="w-full">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="hidden lg:flex items-center gap-4">
          {}
          <Select defaultValue="en">
            <SelectTrigger className="w-[110px] border-none shadow-none bg-transparent focus:ring-0 text-muted-foreground hover:text-foreground transition-colors">
              <div className="flex items-center gap-2">
                <Globe className="size-4" />
                <SelectValue placeholder="Language" />
              </div>
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="en">English</SelectItem>
              <SelectItem value="es">Español</SelectItem>
              <SelectItem value="de">Deutsch</SelectItem>
              <SelectItem value="it">Italiano</SelectItem>
            </SelectContent>
          </Select>

          {}
          <Button asChild className="rounded-[4px] border border-primary bg-transparent text-foreground hover:bg-primary/10 px-6 font-medium uppercase tracking-wider transition-all duration-200">
            <Link to="/DownloadPage">Download</Link>
          </Button>
        </div>

        {}
        <div className="lg:hidden flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-foreground hover:bg-white/5" aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
          </Button>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="lg:hidden absolute top-20 left-0 w-full bg-background border-b border-white/10 shadow-2xl animate-in slide-in-from-top-5 fade-in duration-200">
          <div className="flex flex-col p-4 space-y-6 max-h-[calc(100vh-5rem)] overflow-y-auto">
            <MenuProvider menu_id="16" className="w-full">
              <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>

            <div className="flex flex-col gap-4 px-6 pb-6 border-t border-white/10 pt-6">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground text-sm font-medium">Language</span>
                <Select defaultValue="en">
                  <SelectTrigger className="w-[120px] h-8 bg-white/5 border-white/10">
                    <div className="flex items-center gap-2">
                      <Globe className="size-3" />
                      <SelectValue />
                    </div>
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="es">Español</SelectItem>
                    <SelectItem value="de">Deutsch</SelectItem>
                    <SelectItem value="it">Italiano</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button asChild className="w-full rounded-[4px] border border-primary bg-transparent text-foreground hover:bg-primary/10 uppercase font-medium tracking-wider">
                <Link to="/DownloadPage" onClick={() => setIsMobileMenuOpen(false)}>Download</Link>
              </Button>
            </div>
          </div>
        </div>}
    </header>;
}